import React, { useEffect, useRef, useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Language_Text_Editor } from '../language_text_editor.js';
import Choices from '../../../../components/Choices.js';

/**
 * TableColumnsBuilder Component
 * 
 * Allows users to configure columns for table questions
 * Supports adding, editing, and deleting columns with various properties
 */
const TableColumnsBuilder = ({ 
    columns, 
    language, 
    onColumnsChange, 
    colors,
    // Custom vocabularies support (optional)
    customVocabularies = [],
    loadingCustomVocabularies = false,
    loadCustomVocabularies
}) => {
    const { t } = useTranslation();
    const [editingColumnIndex, setEditingColumnIndex] = useState(null);
    const [editingColumn, setEditingColumn] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newColumn, setNewColumn] = useState({
        name: [],
        type: 'text',
        required: false,
        placeholder: '',
        validation: {},
        options: [],
        selectedCustomVocabulary: null,
        periodIncludeTime: false
    });

    // Auto-load custom vocabularies when user selects the customVocabulary type
    const newTypeRequestedRef = useRef(false);
    const editTypeRequestedRef = useRef(false);

    useEffect(() => {
        if (newColumn?.type === 'customVocabulary') {
            if (!newTypeRequestedRef.current && typeof loadCustomVocabularies === 'function') {
                newTypeRequestedRef.current = true;
                loadCustomVocabularies();
            }
        } else {
            newTypeRequestedRef.current = false;
        }
        // Intentionally NOT depending on loadCustomVocabularies to avoid function identity loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newColumn?.type]);

    useEffect(() => {
        if (editingColumn?.type === 'customVocabulary') {
            if (!editTypeRequestedRef.current && typeof loadCustomVocabularies === 'function') {
                editTypeRequestedRef.current = true;
                loadCustomVocabularies();
            }
        } else {
            editTypeRequestedRef.current = false;
        }
        // Intentionally NOT depending on loadCustomVocabularies to avoid function identity loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingColumn?.type]);

    /**
     * Get localized text from text array
     */
    const getLocalizedText = (textArray, defaultText) => {
        if (!textArray || !Array.isArray(textArray) || textArray.length === 0) return defaultText;
        
        const localizedItem = textArray.find(t => t && t.language === language);
        
        if (!localizedItem || !localizedItem.text || localizedItem.text.trim() === '') {
            const anyLanguageText = textArray.find(t => t && t.text && t.text.trim() !== '');
            return anyLanguageText?.text || defaultText;
        }
        
        return localizedItem.text;
    };

    /**
     * Generate unique column ID
     */
    const generateColumnId = () => {
        return uuidv4();
    };

    /**
     * Add new column
     */
    const addColumn = () => {
        if (newColumn.name.length === 0 || !getLocalizedText(newColumn.name, '').trim()) {
            return;
        }

        const columnToAdd = {
            id: generateColumnId(),
            name: newColumn.name,
            type: newColumn.type,
            required: newColumn.required,
            placeholder: newColumn.placeholder,
            validation: newColumn.validation,
            options: newColumn.options,
            // For custom vocabulary columns
            selectedCustomVocabulary: newColumn.type === 'customVocabulary' ? newColumn.selectedCustomVocabulary : undefined,
            // For period columns
            periodIncludeTime: newColumn.type === 'customVocabulary' ? undefined : (newColumn.type === 'period' ? !!newColumn.periodIncludeTime : undefined)
        };

        onColumnsChange([...columns, columnToAdd]);
        
        // Reset form
        setNewColumn({
            name: [],
            type: 'text',
            required: false,
            placeholder: '',
            validation: {},
            options: [],
            selectedCustomVocabulary: null,
            periodIncludeTime: false
        });
        setShowAddForm(false);
    };

    /**
     * Delete column
     */
    const deleteColumn = (index) => {
        const updated = [...columns];
        updated.splice(index, 1);
        onColumnsChange(updated);
    };

    /**
     * Update column
     */
    const updateColumn = (index) => {
        const updated = [...columns];
        updated[index] = editingColumn;
        onColumnsChange(updated);
        setEditingColumnIndex(null);
        setEditingColumn(null);
    };

    /**
     * Render column type selector
     */
    const renderColumnTypeSelector = (column, onChange) => (
        <select 
            className="form-select form-select-sm"
            value={column.type}
            onChange={(e) => {
                const nextType = e.target.value;
                // Reset custom vocabulary selection when changing type
                const reset = {
                    ...(nextType !== 'customVocabulary' ? { selectedCustomVocabulary: null } : {}),
                    ...(nextType !== 'period' ? { periodIncludeTime: false } : {})
                };
                onChange({ type: nextType, ...reset });
            }}
        >
            <option value="text">{t('text', { ns: 'labels' }) || 'Testo'}</option>
            <option value="number">{t('number', { ns: 'labels' }) || 'Numero'}</option>
            <option value="date">{t('date', { ns: 'labels' }) || 'Data'}</option>
            <option value="time">{t('time', { ns: 'labels' }) || 'Ora'}</option>
            <option value="datetime">{t('datetime', { ns: 'labels' }) || 'Data e Ora'}</option>
            <option value="customVocabulary">{t('customVocabulary', { ns: 'labels' })}</option>
            <option value="period">{t('period', { ns: 'labels' })}</option>
        </select>
    );

    /**
     * Render custom vocabulary selector for columns of type 'customVocabulary'
     */
    const renderCustomVocabularySelector = (column, onChange) => {
        if (column.type !== 'customVocabulary') return null;

        const selectedItem = customVocabularies.find(v => v.Id === column.selectedCustomVocabulary);

        return (
            <div className="mt-2">
                {loadingCustomVocabularies && (
                    <div className="text-muted small mb-2">
                        <i className="fa fa-spinner fa-spin me-1"></i>
                        {t('loadingVocabularies', { ns: 'labels' })}
                    </div>
                )}
                <Choices
                    voc={customVocabularies}
                    value="Id"
                    text="Name"
                    className="inputStyle blackText bg-white"
                    selected={selectedItem ? [selectedItem] : []}
                    onChange={(e) => {
                        const selectedId = e.selected?.[0]?.Id || null;
                        onChange({ selectedCustomVocabulary: selectedId });
                    }}
                    placeholder={t('selectCustomVocabulary', { ns: 'labels' })}
                    dropDownParent="root"
                    onFocus={() => {
                        if (typeof loadCustomVocabularies === 'function') {
                            loadCustomVocabularies();
                        }
                    }}
                />
                <div className="form-text">
                    {t('customVocabularyColumnHelp', { ns: 'labels' })}
                </div>
            </div>
        );
    };

    /**
     * Render period options (include time flag) for columns of type 'period'
     */
    const renderPeriodOptions = (column, onChange) => {
        if (column.type !== 'period') return null;
        return (
            <div className="mt-2">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id={`period-include-time-${column.id || 'new'}`}
                        checked={!!column.periodIncludeTime}
                        onChange={(e) => onChange({ periodIncludeTime: e.target.checked })}
                    />
                    <label className="form-check-label small" htmlFor={`period-include-time-${column.id || 'new'}`}>
                        {t('includeTime', { ns: 'labels' })}
                    </label>
                </div>
            </div>
        );
    };

    /**
     * Render validation options based on column type
     */
    const renderValidationOptions = (column, onChange) => {
        if (column.type === 'text') {
            return (
                <div className="row mt-2">
                    <div className="col-md-6">
                        <label className="form-label small">{t('minLength', { ns: 'labels' }) || 'Lunghezza Min'}</label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={column.validation?.minLength || ''}
                            onChange={(e) => onChange({
                                validation: {
                                    ...column.validation,
                                    minLength: e.target.value ? parseInt(e.target.value) : undefined
                                }
                            })}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small">{t('maxLength', { ns: 'labels' }) || 'Lunghezza Max'}</label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={column.validation?.maxLength || ''}
                            onChange={(e) => onChange({
                                validation: {
                                    ...column.validation,
                                    maxLength: e.target.value ? parseInt(e.target.value) : undefined
                                }
                            })}
                        />
                    </div>
                </div>
            );
        } else if (column.type === 'number') {
            return (
                <div className="row mt-2">
                    <div className="col-md-6">
                        <label className="form-label small">{t('minValue', { ns: 'labels' }) || 'Valore Min'}</label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={column.validation?.min || ''}
                            onChange={(e) => onChange({
                                validation: {
                                    ...column.validation,
                                    min: e.target.value ? parseFloat(e.target.value) : undefined
                                }
                            })}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small">{t('maxValue', { ns: 'labels' }) || 'Valore Max'}</label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={column.validation?.max || ''}
                            onChange={(e) => onChange({
                                validation: {
                                    ...column.validation,
                                    max: e.target.value ? parseFloat(e.target.value) : undefined
                                }
                            })}
                        />
                    </div>
                </div>
            );
        }
        return null;
    };

    /**
     * Render select options editor
     */
    const renderSelectOptionsEditor = (column, onChange) => {
        if (column.type !== 'select') return null;

        const options = column.options || [];

        const addOption = () => {
            const newOption = {
                value: uuidv4(),
                text: [{ language: language, text: '' }]
            };
            onChange({
                options: [...options, newOption]
            });
        };

        const updateOption = (optionIndex, updatedOption) => {
            const updatedOptions = [...options];
            updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], ...updatedOption };
            onChange({
                options: updatedOptions
            });
        };

        const deleteOption = (optionIndex) => {
            const updatedOptions = [...options];
            updatedOptions.splice(optionIndex, 1);
            onChange({
                options: updatedOptions
            });
        };

        return (
            <div className="mt-2">
                <label className="form-label small">{t('selectOptions', { ns: 'labels' }) || 'Opzioni di Selezione'}</label>
                {options.map((option, optionIndex) => (
                    <div key={optionIndex} className="d-flex align-items-center mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm me-2"
                            value={option.value}
                            placeholder={t('optionValue', { ns: 'labels' }) || 'Valore opzione'}
                            onChange={(e) => updateOption(optionIndex, { value: e.target.value })}
                        />
                        <input
                            type="text"
                            className="form-control form-control-sm me-2"
                            value={getLocalizedText(option.text, '')}
                            placeholder={t('optionText', { ns: 'labels' }) || 'Testo opzione'}
                            onChange={(e) => updateOption(optionIndex, {
                                text: [{ language: language, text: e.target.value }]
                            })}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteOption(optionIndex)}
                        >
                            <FaTrash />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addOption}
                >
                    <FaPlus className="me-1" />
                    {t('addOption', { ns: 'labels' }) || 'Aggiungi Opzione'}
                </button>
            </div>
        );
    };

    /**
     * Render column item
     */
    const renderColumnItem = (column, index) => {
        const isEditing = editingColumnIndex === index;

        if (isEditing) {
            return (
                <div key={column.id} className="card mb-3">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <label className="form-label small">{t('columnName', { ns: 'labels' }) || 'Nome Colonna'}</label>
                                <Language_Text_Editor
                                    language={language}
                                    mode="input"
                                    text={editingColumn.name}
                                    onChange={(updated) => setEditingColumn(prev => ({ ...prev, name: updated }))}
                                    colors={colors}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small">{t('columnType', { ns: 'labels' }) || 'Tipo Colonna'}</label>
                                {renderColumnTypeSelector(editingColumn, (update) => setEditingColumn(prev => ({ ...prev, ...update })))}
                            </div>
                        </div>
                        
                        <div className="row mt-2">
                            <div className="col-md-6">
                                <label className="form-label small">{t('placeholder', { ns: 'labels' }) || 'Placeholder'}</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={editingColumn.placeholder || ''}
                                    onChange={(e) => setEditingColumn(prev => ({ ...prev, placeholder: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6 d-flex align-items-center">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={editingColumn.required}
                                        onChange={(e) => setEditingColumn(prev => ({ ...prev, required: e.target.checked }))}
                                    />
                                    <label className="form-check-label small">
                                        {t('required', { ns: 'labels' }) || 'Obbligatorio'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {renderValidationOptions(editingColumn, (update) => setEditingColumn(prev => ({...prev, ...update})))}
                        {renderSelectOptionsEditor(editingColumn, (update) => setEditingColumn(prev => ({...prev, ...update})))}
                        {renderCustomVocabularySelector(editingColumn, (update) => setEditingColumn(prev => ({ ...prev, ...update })))}
                        {renderPeriodOptions(editingColumn, (update) => setEditingColumn(prev => ({ ...prev, ...update })))}

                        <div className="mt-3">
                            <button
                                type="button"
                                className="btn btn-sm btn-success me-2"
                                onClick={() => updateColumn(index)}
                            >
                                <FaCheck className="me-1" />
                                {t('save', { ns: 'labels' }) || 'Salva'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                    setEditingColumnIndex(null);
                                    setEditingColumn(null);
                                }}
                            >
                                <FaTimes className="me-1" />
                                {t('cancel', { ns: 'labels' }) || 'Annulla'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={column.id} className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                <div>
                    <strong>{getLocalizedText(column.name, `${t('column', { ns: 'labels' }) || 'Colonna'} ${index + 1}`)}</strong>
                    <span className="text-muted ms-2">({column.type})</span>
                    {column.required && <span className="text-danger ms-1">*</span>}
                </div>
                <div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                            setEditingColumnIndex(index);
                            setEditingColumn({ ...column });
                        }}
                    >
                        <FaEdit />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteColumn(index)}
                    >
                        <FaTrash />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="table-columns-builder">
            <div className="mb-3">
                <h6>{t('columns', { ns: 'labels' }) || 'Colonne'} ({columns.length})</h6>
                
                {columns.map((column, index) => renderColumnItem(column, index))}
                
                {columns.length === 0 && (
                    <div className="text-muted text-center py-3">
                        {t('noColumnsConfigured', { ns: 'labels' }) || 'Nessuna colonna configurata'}
                    </div>
                )}
            </div>

            {/* Add Column Form */}
            {showAddForm && (
                <div className="card mb-3">
                    <div className="card-header">
                        <h6 className="mb-0">{t('addColumn', { ns: 'labels' }) || 'Aggiungi Colonna'}</h6>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <label className="form-label small">{t('columnName', { ns: 'labels' }) || 'Nome Colonna'}</label>
                                <Language_Text_Editor
                                    language={language}
                                    mode="input"
                                    text={newColumn.name}
                                    onChange={(updated) => setNewColumn(prev => ({ ...prev, name: updated }))}
                                    colors={colors}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small">{t('columnType', { ns: 'labels' }) || 'Tipo Colonna'}</label>
                                {renderColumnTypeSelector(newColumn, (update) => setNewColumn(prev => ({ ...prev, ...update })))}
                            </div>
                        </div>
                        
                        <div className="row mt-2">
                            <div className="col-md-6">
                                <label className="form-label small">{t('placeholder', { ns: 'labels' }) || 'Placeholder'}</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={newColumn.placeholder || ''}
                                    onChange={(e) => setNewColumn(prev => ({ ...prev, placeholder: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6 d-flex align-items-center">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={newColumn.required}
                                        onChange={(e) => setNewColumn(prev => ({ ...prev, required: e.target.checked }))}
                                    />
                                    <label className="form-check-label small">
                                        {t('required', { ns: 'labels' }) || 'Obbligatorio'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {renderValidationOptions(newColumn, (update) => setNewColumn(prev => ({ ...prev, ...update })))}
                        {renderSelectOptionsEditor(newColumn, (update) => setNewColumn(prev => ({ ...prev, ...update })))}
                        {renderCustomVocabularySelector(newColumn, (update) => setNewColumn(prev => ({ ...prev, ...update })))}
                        {renderPeriodOptions(newColumn, (update) => setNewColumn(prev => ({ ...prev, ...update })))}

                        <div className="mt-3">
                            <button
                                type="button"
                                className="btn btn-sm btn-success me-2"
                                onClick={addColumn}
                            >
                                <FaCheck className="me-1" />
                                {t('add', { ns: 'labels' }) || 'Aggiungi'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setShowAddForm(false)}
                            >
                                <FaTimes className="me-1" />
                                {t('cancel', { ns: 'labels' }) || 'Annulla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Column Button */}
            {!showAddForm && (
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowAddForm(true)}
                >
                    <FaPlus className="me-1" />
                    {t('addColumn', { ns: 'labels' }) || 'Aggiungi Colonna'}
                </button>
            )}
        </div>
    );
};

export default TableColumnsBuilder; 